import { useEffect, useRef, useState } from 'react';
import { Bold, Underline, ListPlus } from 'lucide-react';
import { toast } from 'sonner';
import { FormulesRemarquesDialog } from './FormulesRemarquesDialog';

// Éditeur des remarques devis : le texte s'affiche directement en gras / souligné
// (comme dans le PDF). Stockage sous forme de marqueurs **gras** / __souligné__.

const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const markupToHtml = (value) => (value || '').split('\n').map((line) => {
    let html = '', bold = false, und = false, buf = '', i = 0;
    const flush = () => {
        if (!buf) return;
        let s = escapeHtml(buf);
        if (bold) s = `<b>${s}</b>`;
        if (und) s = `<u>${s}</u>`;
        html += s; buf = '';
    };
    while (i < line.length) {
        if (line.startsWith('**', i)) { flush(); bold = !bold; i += 2; continue; }
        if (line.startsWith('__', i)) { flush(); und = !und; i += 2; continue; }
        buf += line[i]; i += 1;
    }
    flush();
    return `<div>${html || '<br>'}</div>`;
}).join('');

const isBold = (el) => ['B', 'STRONG'].includes(el.nodeName) || /^(bold|[6-9]00)$/.test(el.style?.fontWeight || '');
const isUnderline = (el) => el.nodeName === 'U' || (el.style?.textDecoration || '').includes('underline');
const isBlock = (el) => ['DIV', 'P', 'LI'].includes(el.nodeName);

const htmlToMarkup = (root) => {
    const runs = [];
    const walk = (node, bold, und) => {
        if (node.nodeType === 3) { if (node.nodeValue) runs.push({ text: node.nodeValue, bold, und }); return; }
        if (node.nodeType !== 1) return;
        if (node.nodeName === 'BR') {
            const p = node.parentNode;
            const trailing = p && p !== root && isBlock(p) && p.lastChild === node && p.childNodes.length > 1;
            if (!trailing) runs.push({ text: '\n' });
            return;
        }
        const b = bold || isBold(node), u = und || isUnderline(node);
        if (isBlock(node) && runs.length && !runs[runs.length - 1].text.endsWith('\n')) runs.push({ text: '\n' });
        node.childNodes.forEach((c) => walk(c, b, u));
    };
    root.childNodes.forEach((c) => walk(c, false, false));

    let out = '', curB = false, curU = false;
    const close = () => { if (curU) { out += '__'; curU = false; } if (curB) { out += '**'; curB = false; } };
    runs.forEach((r) => {
        if (r.text === '\n') { close(); out += '\n'; return; }
        const b = !!r.bold, u = !!r.und;
        if (b !== curB || u !== curU) {
            close();
            if (b) { out += '**'; curB = true; }
            if (u) { out += '__'; curU = true; }
        }
        out += r.text.replace(/\u00a0/g, ' ');
    });
    close();
    return out.replace(/\n+$/, '');
};

export function RemarquesEditor({ value, onChange, placeholder, className = '', dataTestId = 'remarques-devis-editor' }) {
    const ref = useRef(null);
    const lastEmitted = useRef(null);

    useEffect(() => {
        if (!ref.current) return;
        if ((value || '') !== (lastEmitted.current ?? '')) {
            ref.current.innerHTML = markupToHtml(value);
            lastEmitted.current = value || '';
        }
    }, [value]);

    const emit = () => {
        if (!ref.current) return;
        const md = htmlToMarkup(ref.current);
        lastEmitted.current = md;
        onChange(md);
    };

    const exec = (cmd) => {
        ref.current?.focus();
        document.execCommand(cmd, false, null);
        emit();
    };

    const handlePaste = (e) => {
        e.preventDefault();
        document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
    };

    const empty = !(value || '').trim();

    // Formules préparées : ajout une par une en fin de texte (une ligne chacune)
    const [formulesOpen, setFormulesOpen] = useState(false);
    const addFormule = (texte) => {
        const current = (value || '').replace(/\s+$/, '');
        if (current.includes(texte)) {
            toast.info('Cette formule est déjà dans les remarques');
            return;
        }
        onChange(current ? `${current}\n${texte}` : texte);
        toast.success('Formule ajoutée aux remarques');
    };

    return (
        <div className={className}>
            <div className="flex items-center gap-1 mb-2" data-testid="remarques-devis-toolbar">
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} title="Gras (sélectionnez du texte)" className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:border-[#2e7cd6]" data-testid="remarques-devis-bold-btn">
                    <Bold size={14} />
                </button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} title="Souligné (sélectionnez du texte)" className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:border-[#2e7cd6]" data-testid="remarques-devis-underline-btn">
                    <Underline size={14} />
                </button>
                <button type="button" onClick={() => setFormulesOpen(true)} title="Ajouter des formules préparées" className="ml-auto h-8 px-3 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-100 hover:border-[#2e7cd6]" data-testid="remarques-devis-formules-btn">
                    <ListPlus size={14} /> Formules
                </button>
            </div>
            <FormulesRemarquesDialog open={formulesOpen} onOpenChange={setFormulesOpen} onAdd={addFormule} />
            <div className="relative">
                {empty && (
                    <span className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400" data-testid="remarques-devis-placeholder">{placeholder}</span>
                )}
                <div
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={emit}
                    onBlur={emit}
                    onPaste={handlePaste}
                    className="w-full min-h-[260px] resize-y overflow-auto rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 leading-6 focus:outline-none focus:ring-2 focus:ring-[#2e7cd6]/40 focus:border-[#2e7cd6] whitespace-pre-wrap break-words"
                    data-testid={dataTestId}
                />
            </div>
        </div>
    );
}
